import * as React from 'react'
import cx from 'classnames'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { observer } from 'mobx-react'
import styles from './AlertsHost.sass'
import AlertsController from 'alerts/AlertsController'
import { Alert, AlertState } from 'alerts/Alert'
import { match } from 'utils/pattern-match-util'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'

const ANIMATION_DURATION = 500

interface AlertsHostProps {
  controller: AlertsController
}

export default observer(function AlertsHost({ controller }: AlertsHostProps) {
  return (
    <div className={cx(styles.root)}>
      <TransitionGroup component={null}>
        {controller.alerts.map((alert) => (
          <CSSTransition
            key={alert.id}
            timeout={ANIMATION_DURATION}
            appear
            classNames={{
              enter: styles.animationEnter,
              appear: styles.animationEnter,
              exit: styles.animationLeave,
            }}
          >
            <AlertView alert={alert} />
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  )
})

interface AlertViewProps {
  alert: Alert
}

const AlertView = observer(function AlertView({ alert }: AlertViewProps) {
  return (
    <div className={cx(styles.alert)}>
      <div
        onClick={alert.interact}
        onMouseEnter={alert.cancelAutoDismiss}
        data-test={`alert-${alert.title}`}
        className={cx(
          styles.box,
          styles[alert.state],
          // On macOS, when there are no actions on Notifications, the "close" button
          // reveals itself on hover.
          alert.actions.length === 0 && styles.hideActionsUntilHover,
        )}
      >
        <div className={styles.iconContainer}>
          {match(alert.state, {
            [AlertState.Neutral]: () => <Icon auto name="info" />,
            [AlertState.Pending]: () => <Spinner color="#000" size={20} />,
            [AlertState.Success]: () => <Icon auto name="tick" />,
            [AlertState.Warning]: () => <Icon auto name="info" rotate={180} />,
            [AlertState.Error]: () => <Icon auto name="info" rotate={180} />,
          })}
        </div>
        <div className={styles.content}>
          {alert.title && <p className={styles.title}>{alert.title}</p>}
          {alert.description && (
            <p title={alert.description} className={styles.description}>
              {alert.description}
            </p>
          )}
        </div>
        <div className={styles.actions}>
          {alert.actions.length === 0 ? (
            <AlertAction
              title="Close"
              onClick={alert.dismiss}
              data-test={`alert-${alert.title}-button-Close`}
            >
              Close
            </AlertAction>
          ) : (
            alert.actions.map((action, idx) => (
              <AlertAction
                key={idx}
                title={action.title}
                onClick={() => alert.triggerAction(action)}
                data-test={`alert-${alert.title}-button-${action.title}`}
              >
                {action.title}
              </AlertAction>
            ))
          )}
        </div>
      </div>
    </div>
  )
})

interface AlertActionProps {
  onClick(): unknown
  children: React.ReactNode
  title: string
  ['data-test']?: string
}

const AlertAction = ({
  onClick,
  children,
  title,
  'data-test': dataTest,
}: AlertActionProps) => (
  <a
    className={styles.action}
    title={title}
    data-test={dataTest}
    onClick={(e) => {
      e.preventDefault()
      e.stopPropagation()
      onClick()
    }}
  >
    <span className={styles.actionText}>{children}</span>
  </a>
)
